if (!customElements.get('cinoton-hero')) {
  class CinotonHero extends HTMLElement {
    constructor() {
      super();
      this.requestToken = 0;
      this.handlePreviousClick = this.handlePreviousClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handlePlayClick = this.handlePlayClick.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      this.counter = this.querySelector('[data-hero-counter]');
      this.playButton = this.querySelector('[data-hero-playpause]');
      this.previousButton = this.querySelector('[data-hero-prev]');
      this.nextButton = this.querySelector('[data-hero-next]');
      this.interval = Number(this.dataset.autoplay || 0);
      this.userPaused = this.interval === 0;
      this.interactionPaused = false;
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.autoplayUnlocked = !this.interval || !this.mobileQuery.matches;
      this.unlockAutoplayBound = (event) => {
        if (!event.isTrusted) return;
        if (event.target instanceof Node && this.contains(event.target)) return;
        this.unlockAutoplay();
      };
      this.mobileChangeBound = (event) => {
        if (!event.matches) this.unlockAutoplay();
      };
      this.index = 0;
      this.preloads = new WeakMap();
      this.previousButton?.addEventListener('click', this.handlePreviousClick);
      this.nextButton?.addEventListener('click', this.handleNextClick);
      this.playButton?.addEventListener('click', this.handlePlayClick);
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.show(0);
      if (this.autoplayUnlocked) this.prepareSlide(1);
      else this.bindAutoplayUnlock();
      this.updatePlayButton();
      this.start();
    }

    disconnectedCallback() {
      this.requestToken += 1;
      this.stop();
      this.removeAutoplayUnlock();
      this.previousButton?.removeEventListener('click', this.handlePreviousClick);
      this.nextButton?.removeEventListener('click', this.handleNextClick);
      this.playButton?.removeEventListener('click', this.handlePlayClick);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }

    handlePreviousClick(event) {
      if (!event.isTrusted) return;
      this.unlockAutoplay();
      this.requestShow(this.index - 1);
    }

    handleNextClick(event) {
      if (!event.isTrusted) return;
      this.unlockAutoplay();
      this.requestShow(this.index + 1);
    }

    handlePlayClick(event) {
      if (!event.isTrusted) return;
      if (!this.autoplayUnlocked) {
        this.interactionPaused = false;
        this.unlockAutoplay();
        return;
      }
      this.userPaused = !this.userPaused;
      this.updatePlayButton();
      if (this.userPaused) this.stop();
      else {
        this.interactionPaused = false;
        this.start();
      }
    }

    handleMouseEnter() {
      this.interactionPaused = true;
      this.stop();
    }

    handleMouseLeave() {
      this.interactionPaused = false;
      this.start();
    }

    handleFocusIn() {
      this.interactionPaused = true;
      this.stop();
    }

    handleFocusOut(event) {
      if (!this.contains(event.relatedTarget)) {
        this.interactionPaused = false;
        this.start();
      }
    }

    handleBlockSelect(event) {
      const index = this.slides.indexOf(event.target.closest('[data-hero-slide]'));
      this.userPaused = true;
      this.updatePlayButton();
      this.stop();
      if (index >= 0) this.requestShow(index);
    }

    bindAutoplayUnlock() {
      window.addEventListener('pointerdown', this.unlockAutoplayBound, { passive: true, capture: true });
      window.addEventListener('touchstart', this.unlockAutoplayBound, { passive: true, capture: true });
      window.addEventListener('wheel', this.unlockAutoplayBound, { passive: true, capture: true });
      window.addEventListener('keydown', this.unlockAutoplayBound);
      if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', this.mobileChangeBound);
      else this.mobileQuery.addListener?.(this.mobileChangeBound);
    }

    removeAutoplayUnlock() {
      window.removeEventListener('pointerdown', this.unlockAutoplayBound, true);
      window.removeEventListener('touchstart', this.unlockAutoplayBound, true);
      window.removeEventListener('wheel', this.unlockAutoplayBound, true);
      window.removeEventListener('keydown', this.unlockAutoplayBound);
      if (this.mobileQuery?.removeEventListener) this.mobileQuery.removeEventListener('change', this.mobileChangeBound);
      else this.mobileQuery?.removeListener?.(this.mobileChangeBound);
    }

    unlockAutoplay() {
      if (this.autoplayUnlocked || !this.interval) return;
      this.autoplayUnlocked = true;
      this.removeAutoplayUnlock();
      this.prepareSlide(this.index + 1);
      this.updatePlayButton();
      this.start();
    }

    imageReady(image) {
      const decode = () => typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
      if (image.complete) return image.naturalWidth ? decode() : Promise.resolve();
      return new Promise((resolve) => {
        image.addEventListener('load', () => resolve(decode()), { once: true });
        image.addEventListener('error', resolve, { once: true });
      });
    }

    hydrateImage(image) {
      image.closest('picture')?.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        delete source.dataset.srcset;
      });
      if (image.dataset.srcset) {
        image.srcset = image.dataset.srcset;
        delete image.dataset.srcset;
      }
      if (image.dataset.src) {
        image.src = image.dataset.src;
        delete image.dataset.src;
      }
    }

    prepareSlide(index) {
      if (!this.slides.length) return Promise.resolve();
      const slide = this.slides[(index + this.slides.length) % this.slides.length];
      if (this.preloads.has(slide)) return this.preloads.get(slide);
      const images = [...slide.querySelectorAll('img')];
      images.forEach((image) => {
        this.hydrateImage(image);
        image.loading = 'eager';
      });
      const ready = Promise.all(images.map((image) => this.imageReady(image))).then(() => slide);
      this.preloads.set(slide, ready);
      return ready;
    }

    async requestShow(next, { autoplay = false } = {}) {
      if (!this.slides.length) return;
      const index = (next + this.slides.length) % this.slides.length;
      const token = ++this.requestToken;
      this.stop();
      await this.prepareSlide(index);
      if (token !== this.requestToken || !this.isConnected || (autoplay && (!this.autoplayUnlocked || this.userPaused || this.interactionPaused))) return;
      this.show(index);
      this.prepareSlide(index + 1);
      this.start();
    }

    show(next) {
      if (!this.slides.length) return;
      this.index = (next + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a').forEach((link) => { link.tabIndex = active ? 0 : -1; });
      });
      if (this.counter) {
        this.counter.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
      }
    }

    start() {
      this.stop();
      if (!this.autoplayUnlocked || this.userPaused || this.interactionPaused || !this.interval || this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.Shopify?.designMode) return;
      this.timer = window.setTimeout(() => this.requestShow(this.index + 1, { autoplay: true }), this.interval);
    }

    stop() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;
    }

    updatePlayButton() {
      if (!this.playButton) return;
      const paused = this.userPaused || !this.autoplayUnlocked;
      this.playButton.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      this.playButton.setAttribute('aria-pressed', String(paused));
      this.playButton.dataset.paused = String(paused);
    }
  }
  customElements.define('cinoton-hero', CinotonHero);
}
